/**
 * LookupAdmin — surface 4 (lookup-table editorial workflow standalone page)
 * (Block 6.C.1 / ADR-020 + ADR-017 + ADR-024).
 *
 * Wireframe at design plan §4 (`Phase 1\Block 6.C.1 design plan 2026-05-08 r2.md`).
 *
 * Behavior:
 *   - Tabs across the three editable lookup tables (subcategories /
 *     specialties / service-areas). Active tab driven by `?type=` URL
 *     search param; default subcategories.
 *   - Toggle "Show deprecated" via `?include=deprecated` URL param.
 *   - Add affordance at top of list (LookupAdminAddRow).
 *   - Each row tap-to-expand for inline edit / rename slug / deprecate /
 *     restore / initiate merge (LookupAdminRow).
 *   - Merge state managed at this page level: source row + target row +
 *     LookupMergePreview pane above the list. Subcategories + specialties
 *     only (ADR-020 §1; service-areas merge defers to Era B).
 *   - Non-steward access → server returns 403 on the LIST endpoint; UI
 *     renders ErrorBanner + back-to-directory link.
 *
 * ADR-017 rubric: F1 (no modal); F2 (single page); F4 (always-visible
 * affordances); F7 (deprecate is reversible — no confirm; merge IS
 * destructive but uses inline-confirmation pane, not a Dialog).
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import { lookupsApi, canAddLookup } from '@/lib/lookups';
import {
  type HighlightAdminRow,
  type LookupAdminRow as LookupAdminRowData,
  type LookupTypeUrl,
  type ServiceAreaAdminRow,
} from '@/lib/types';
import { AsyncBoundary } from '@/components/primitives/AsyncBoundary';
import { LookupAdminTabs } from '@/components/LookupAdminTabs';
import { LookupAdminAddRow } from '@/components/LookupAdminAddRow';
import { LookupAdminRow } from '@/components/LookupAdminRow';
import { LookupMergePreview } from '@/components/LookupMergePreview';
import { lookupsAdminKey } from '@/lib/refreshFamily';
import { useSupportContact } from '@/hooks/useSupportContact';
import { contactSentence } from '@/lib/errorCopy';

const VALID_TYPES: ReadonlyArray<LookupTypeUrl> = [
  'subcategories',
  'specialties',
  'highlights',
  'service-model',
  'service-areas',
];

const TYPE_TO_SINGULAR_LABEL: Record<LookupTypeUrl, string> = {
  subcategories: 'subcategory',
  specialties: 'specialty',
  highlights: 'feature',
  'service-model': 'delivery method',
  'service-areas': 'service area',
};

// Phase 1.10 — Features tab family-grouped section labels (r2
// disposition #4). The map is order-stable (Medicaid → Memory Care →
// Gender Restrictions) per the FEATURES_FILTER_GROUPS order in the
// directory FilterBar.
const HIGHLIGHT_FAMILY_LABELS: Record<HighlightAdminRow['family'], string> = {
  medicaid: 'Medicaid',
  memory_care: 'Memory Care',
  gender_restrictions: 'Gender Restrictions',
  legacy: 'Legacy (hidden)',
};

const HIGHLIGHT_FAMILY_ORDER: ReadonlyArray<HighlightAdminRow['family']> = [
  'medicaid',
  'memory_care',
  'gender_restrictions',
  'legacy',
];

export function LookupAdmin(): ReactNode {
  const { tenantSlug: routeTenantSlug } = useParams<{ tenantSlug: string }>();
  const { whoami } = useTenant();
  const contact = useSupportContact();
  const [searchParams, setSearchParams] = useSearchParams();

  const tenantSlug = routeTenantSlug ?? '';
  const typeParam = searchParams.get('type');
  const activeType: LookupTypeUrl = VALID_TYPES.includes(typeParam as LookupTypeUrl)
    ? (typeParam as LookupTypeUrl)
    : 'subcategories';
  const includeDeprecated = searchParams.get('include') === 'deprecated';

  // Merge state — page-level. mergeSource is set when user taps "Initiate
  // merge" on a row (subcategories or specialties). mergeTarget is set
  // when user picks a target row from the list.
  const [mergeSource, setMergeSource] = useState<LookupAdminRowData | null>(null);
  const [mergeTarget, setMergeTarget] = useState<LookupAdminRowData | null>(null);

  const isSteward = canAddLookup(whoami);

  // Type-specific list query.
  const invalidationKey = useMemo(
    () => lookupsAdminKey(tenantSlug, activeType, includeDeprecated),
    [tenantSlug, activeType, includeDeprecated],
  );

  const listQuery = useQuery<{ rows: Array<LookupAdminRowData | ServiceAreaAdminRow | HighlightAdminRow> }>({
    queryKey: invalidationKey,
    queryFn: async ({ signal }) => {
      const params = { include_inactive: includeDeprecated, limit: 50 };
      if (activeType === 'subcategories') {
        return lookupsApi.listAdminSubcategories(tenantSlug, params, signal);
      }
      if (activeType === 'specialties') {
        return lookupsApi.listAdminSpecialties(tenantSlug, params, signal);
      }
      if (activeType === 'highlights') {
        return lookupsApi.listAdminHighlights(tenantSlug, params, signal);
      }
      if (activeType === 'service-model') {
        return lookupsApi.listAdminServiceModels(tenantSlug, params, signal);
      }
      return lookupsApi.listAdminServiceAreas(tenantSlug, params, signal);
    },
    enabled: isSteward,
  });

  // ---- Steward gate: render 403 surface for non-stewards ----
  // Server enforces independently on every write. The UI gate avoids
  // surfacing a page that always 403s on read.
  if (!isSteward) {
    return (
      <div className="px-3 py-6">
        <h2 className="font-display text-lg text-sage-800 mb-2">Access denied</h2>
        <div
          role="alert"
          className="my-3 p-3 border border-pepper/30 bg-pepper-soft rounded-base text-sm text-ink"
        >
          {contactSentence(
            "This page is for the people who look after the Directory. Ask {contact_admin} if your work needs it.",
            contact,
          )}
        </div>
      </div>
    );
  }

  function handleTabChange(next: LookupTypeUrl): void {
    // Cancel any in-flight merge when changing tabs.
    setMergeSource(null);
    setMergeTarget(null);
    const sp = new URLSearchParams(searchParams);
    sp.set('type', next);
    setSearchParams(sp, { replace: true });
  }

  function handleToggleIncludeDeprecated(): void {
    const sp = new URLSearchParams(searchParams);
    if (includeDeprecated) {
      sp.delete('include');
    } else {
      sp.set('include', 'deprecated');
    }
    setSearchParams(sp, { replace: true });
  }

  function handleInitiateMerge(row: LookupAdminRowData): void {
    setMergeSource(row);
    setMergeTarget(null);
  }

  function handleCancelMerge(): void {
    setMergeSource(null);
    setMergeTarget(null);
  }

  function handleMergeComplete(): void {
    setMergeSource(null);
    setMergeTarget(null);
  }

  function handlePickTarget(row: LookupAdminRowData): void {
    setMergeTarget(row);
  }

  const rows = listQuery.data?.rows ?? [];
  const inMergeMode = mergeSource !== null;

  // Merge type narrowing: subcategories + specialties + highlights allowed
  // (service-areas excluded per ADR-020 §1).
  const mergeType: 'subcategories' | 'specialties' | 'highlights' | null =
    activeType === 'subcategories' || activeType === 'specialties' || activeType === 'highlights'
      ? activeType
      : null;

  return (
    <div className="px-3 py-4 max-w-3xl mx-auto">
      <h2 className="font-display text-lg text-sage-800 mb-3">Categories &amp; tags</h2>

      <LookupAdminTabs activeType={activeType} onChange={handleTabChange} />

      {/* Include-deprecated toggle */}
      <div className="flex items-center justify-between mb-3 text-xs text-ink-soft">
        <div>
          {listQuery.isSuccess && (
            <>
              Showing {rows.length} {includeDeprecated ? 'rows' : 'active rows'}
            </>
          )}
        </div>
        <label className="inline-flex items-center gap-2 min-h-11 cursor-pointer">
          <input
            type="checkbox"
            checked={includeDeprecated}
            onChange={handleToggleIncludeDeprecated}
            className="w-4 h-4"
            aria-label="Show hidden rows"
          />
          <span>Show hidden</span>
        </label>
      </div>

      {/* Merge state pane */}
      {inMergeMode && mergeSource !== null && mergeType !== null && (
        <LookupMergePreview
          tenantSlug={tenantSlug}
          type={mergeType}
          source={mergeSource}
          target={mergeTarget}
          invalidationKey={invalidationKey}
          onCancel={handleCancelMerge}
          onMergeComplete={handleMergeComplete}
        />
      )}

      {/* Add affordance — hidden during merge to keep the surface focused.
          Phase 1.10 — delivery-method add-affordance deferred to a follow-up
          sub-block (catalog seeded by migration 0069; the 9 atomic
          delivery models cover the empirical vocabulary). The tab still
          renders the read-side admin list for visibility. */}
      {!inMergeMode && activeType !== 'service-model' && (
        <div className="mb-3">
          <LookupAdminAddRow
            tenantSlug={tenantSlug}
            type={activeType}
            typeLabel={TYPE_TO_SINGULAR_LABEL[activeType]}
            invalidationKey={invalidationKey}
          />
        </div>
      )}
      {activeType === 'service-model' && (
        <div className="mb-3 px-3 py-2 text-xs text-ink-soft bg-sand border border-sage-800/[8%] rounded-md">
          The delivery-method catalog is migration-seeded. Inline add /
          deprecate / merge affordances ship in a follow-up sub-block.
        </div>
      )}

      {/* List body — the three non-content states through the one primitive (UX-12). */}
      <AsyncBoundary
        isLoading={listQuery.isPending}
        isError={listQuery.isError}
        error={listQuery.error}
        onRetry={() => void listQuery.refetch()}
        isEmpty={listQuery.isSuccess && rows.length === 0}
        emptyHeadline="No rows yet"
      >
      {listQuery.isSuccess && activeType === 'highlights' ? (
        // Phase 1.10 — Features tab renders family-grouped section
        // headers (Medicaid / Memory Care / Gender Restrictions) per r2
        // disposition #4.
        HIGHLIGHT_FAMILY_ORDER.map((family) => {
          const familyRows = (rows as HighlightAdminRow[]).filter(
            (r) => r.family === family,
          );
          if (familyRows.length === 0) return null;
          return (
            <div key={family} className="mb-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-soft px-2 py-1 mb-1 border-b border-sage-800/[8%]">
                {HIGHLIGHT_FAMILY_LABELS[family]}
              </h3>
              {familyRows.map((row) => {
                const isSource = inMergeMode && mergeSource?.id === row.id;
                const showAsTarget =
                  inMergeMode &&
                  mergeSource?.id !== row.id &&
                  mergeTarget === null &&
                  mergeType !== null;
                return (
                  <LookupAdminRow
                    key={row.id}
                    tenantSlug={tenantSlug}
                    type={activeType}
                    row={row}
                    invalidationKey={invalidationKey}
                    onInitiateMerge={
                      !inMergeMode && mergeType !== null ? handleInitiateMerge : undefined
                    }
                    isMergeSource={isSource}
                    mergePickHandler={
                      showAsTarget ? () => handlePickTarget(row) : undefined
                    }
                  />
                );
              })}
            </div>
          );
        })
      ) : (
        listQuery.isSuccess &&
        rows.map((row) => {
          const rowTypeAdmin = row as LookupAdminRowData;
          const isSource = inMergeMode && mergeSource?.id === row.id;
          const showAsTarget =
            inMergeMode && mergeSource?.id !== row.id && mergeTarget === null && mergeType !== null;
          return (
            <LookupAdminRow
              key={row.id}
              tenantSlug={tenantSlug}
              type={activeType}
              row={row}
              invalidationKey={invalidationKey}
              onInitiateMerge={!inMergeMode && mergeType !== null ? handleInitiateMerge : undefined}
              isMergeSource={isSource}
              mergePickHandler={
                showAsTarget ? () => handlePickTarget(rowTypeAdmin) : undefined
              }
            />
          );
        })
      )}
      </AsyncBoundary>
    </div>
  );
}
